import { Button, ButtonStyles, Dropdown, FilterVariantProps, Icons, NestedSelectList } from '@silverfort/sf-components';
import { useMemo, useState } from 'react';
import { NestedSelectListOptions } from '@silverfort/sf-components/dist/NestedSelectList/interfaces';

export interface FilterOption<T = string> extends FilterVariantProps<T> {
    filterIdentifier: string;
}

interface Props<T = string> {
    allFilterOptions: FilterOption<T>[];
}

const Filters = <T,>({ allFilterOptions }: Props<T>) => {
    const [addedFilters, setAddedFilters] = useState<string[]>([]);
    const defaultActiveFilters = useMemo(
        () => allFilterOptions.slice(0, 3).map((filter) => filter.filterIdentifier),
        [allFilterOptions]
    );

    const getOnFilterRemoveFunc = (filterKey: string) => {
        return () => {
            allFilterOptions?.find((filter) => filter.filterIdentifier === filterKey)?.onChange?.(undefined);
            setAddedFilters((prevFilters) => prevFilters.filter((filter) => filter !== filterKey));
        };
    };

    const onFilterAdded = (filterKey: string | string[] | undefined) => {
        setAddedFilters((prevFilters) => [...prevFilters, filterKey as string]);
    };

    const possibleFiltersToAdd: NestedSelectListOptions = allFilterOptions
        .filter(
            (filter) =>
                !addedFilters.includes(filter.filterIdentifier) &&
                !defaultActiveFilters.includes(filter.filterIdentifier)
        )
        .reduce((acc, filter) => {
            acc[filter.filterIdentifier] = {
                ...filter,
                onChange: (value: T | T[] | undefined) => {
                    filter.onChange?.(value);
                    onFilterAdded(filter.filterIdentifier);
                },
                multiple: true
            };
            return acc;
        }, {});

    const displayedFilters = allFilterOptions.filter(
        (option) =>
            addedFilters.includes(option.filterIdentifier) || defaultActiveFilters.includes(option.filterIdentifier)
    );

    return (
        <div className="w-[80%] h-fit flex flex-wrap gap-2 py-1">
            {displayedFilters.map((filter) => {
                const isRemovable =
                    !defaultActiveFilters.includes(filter.filterIdentifier) ||
                    (Array.isArray(filter.selectedItems) ? filter.selectedItems?.length > 0 : false);

                return (
                    <Dropdown
                        {...filter}
                        key={filter.filterIdentifier}
                        multiple
                        removable={isRemovable}
                        onRemove={
                            defaultActiveFilters.includes(filter.filterIdentifier)
                                ? () => filter.onChange?.(undefined)
                                : getOnFilterRemoveFunc(filter.filterIdentifier)
                        }
                        className="!w-max"
                        searchable
                    />
                );
            })}
            {!!Object.keys(possibleFiltersToAdd).length && (
                <NestedSelectList
                    options={possibleFiltersToAdd}
                    triggerProps={{
                        iconLeft: Icons.Tune,
                        text: 'Add Filter',
                        buttonStyle: ButtonStyles.GhostSolid
                    }}
                    TriggerComponent={Button}
                />
            )}
        </div>
    );
};

export default Filters;
